"use client";

import React from "react";
import { Instagram, Twitter, Linkedin, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const socialLinks = [{
  icon: Instagram,
  url: "#",
  label: "Instagram"
}, {
  icon: Twitter,
  url: "#",
  label: "Twitter"
}, {
  icon: Linkedin,
  url: "#",
  label: "LinkedIn"
}];
const contactDetails = [{
  label: "DIRECT CONTACT",
  value: "Hello@aiCommercials.ai"
}, {
  label: "STUDIO LINE",
  value: "+1 (305) 407-5615\xA0"
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="225677" className="flex flex-col items-center justify-center p-8 text-center h-full min-h-[400px]">
        <div className="size-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(0,255,255,0.4)]">
          <svg className="size-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2 font-default uppercase tracking-tight">Script Received</h3>
        <p className="text-muted-foreground mb-8 font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-primary/30 text-foreground hover:bg-primary/10 hover:text-primary transition-all font-default">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "bg-transparent border-0 border-b border-muted-foreground/30 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary transition-all rounded-none";
  const labelClasses = "text-xs font-medium uppercase tracking-widest text-muted-foreground mb-1 font-default";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-2 font-default uppercase tracking-tighter">Project Inquiry</h2>
        <p className="text-muted-foreground text-sm font-default">Submit your vision and our production team will reach out shortly.</p>
      </div>

      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Christopher Nolan" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60 text-xs font-default">The primary point of contact for this production.</FormDescription>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Business Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="director@studio.com" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60 text-xs font-default">We'll use this for all official project correspondence.</FormDescription>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Direct Line</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60 text-xs font-default">Optional – for urgent production scheduling.</FormDescription>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Project Scope</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your cinematic goals, budget range, and timeline..." className={`min-h-[120px] resize-none ${inputClasses}`} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60 text-xs font-default">Provide as much detail as possible for a faster quote.</FormDescription>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:opacity-90 font-bold uppercase tracking-widest mt-4 rounded-none transition-all shadow-[0_0_20px_rgba(0,255,255,0.2)]">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Processing...
          </> : <>
            Initiate Project
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function Contact() {
  return <section id="contact-form" className="w-full bg-background text-foreground">
      <div className="w-full bg-background py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          
          <div className="flex flex-col justify-center space-y-12">
            <div className="space-y-10">
              {contactDetails.map((detail, i) => <div key={detail.label} data-index={i} className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3 font-default">
                    {detail.label}
                  </span>
                  <span className="text-3xl md:text-4xl font-bold font-default tracking-tighter text-foreground">
                    {detail.value}
                  </span>
                </div>)}
            </div>

            <div className="pt-8 border-t border-border">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-8 block font-default">
                STUDIO CHANNELS
              </span>
              <div className="flex items-center gap-x-8">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.label} to={social.url} data-index={i} className="text-foreground hover:text-primary transition-colors duration-300" aria-label={social.label}>
                      <Icon className="size-6" />
                    </Link>;
              })}
              </div>
            </div>
          </div>

          <div className="relative">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" className="bg-card/60 backdrop-blur-xl border border-primary/10 p-8 md:p-12 rounded-2xl shadow-2xl" sectionName="Contact" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Initiate Project" successMessage="Thank you. Our producers have received your inquiry and will contact you within 24 business hours.">
              <ContactFormContent />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}